// SHCT-694 — PRD 15 §8.1 and [공통] 00_입력검증표준 §2.5 both require that a failed
// validation move focus to the offending field. Forms showed the inline message
// and blocked the request correctly, but focus stayed on the button that was
// clicked, so a screen-reader user got no cue about where the error is.
//
// Driven off the rendered DOM rather than a per-form ref map: `.form-field__error`
// is the one markup contract every panel in this app already follows (it is the
// error element the error-handling standard §7.1 mandates for Validation Errors),
// so a form opts in by passing its container — no field-order list to keep in
// sync as fields are added.

const FOCUSABLE_CONTROL =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]';

/**
 * Focus the control of the first field showing an inline validation error.
 * Returns true when something was focused.
 *
 * DOM order decides "first", which is also the visual order the operator scans.
 * Checkbox groups (e.g. Role) are covered too: their error <p> lives in the same
 * `.form-field`, so the first checkbox in the group receives focus.
 */
export function focusFirstInvalidField(container: HTMLElement | null): boolean {
  if (!container) return false;
  const errorNodes = container.querySelectorAll('.form-field__error');
  for (const errorNode of Array.from(errorNodes)) {
    const field = errorNode.closest('.form-field');
    const control = field?.querySelector<HTMLElement>(FOCUSABLE_CONTROL);
    if (control) {
      control.focus();
      // Panels scroll independently of the page; a field below the fold would
      // otherwise take focus without ever coming into view.
      control.scrollIntoView({ block: 'nearest' });
      return true;
    }
  }
  return false;
}

/**
 * Same, deferred to the next frame — for the usual call site, right after a
 * `setState` that renders the error messages. The nodes this reads do not exist
 * until React has committed that render.
 */
export function focusFirstInvalidFieldAfterRender(getContainer: () => HTMLElement | null): void {
  requestAnimationFrame(() => focusFirstInvalidField(getContainer()));
}
